/**
 * Lightweight haptic feedback wrapper.
 *
 * Uses navigator.vibrate when available (iOS/Android web view + Android browsers).
 * On iOS Safari/WKWebView, vibrate is not always supported, but Capacitor exposes
 * the system haptic engine. We try Capacitor first if available, then fall back.
 *
 * Style values mirror Apple's UIImpactFeedbackStyle: light, medium, heavy, success, warning, error.
 */

let capacitorHapticsModule = null;
let capacitorHapticsCheckDone = false;

async function getCapacitorHaptics() {
  if (capacitorHapticsCheckDone) return capacitorHapticsModule;
  capacitorHapticsCheckDone = true;
  try {
    if (window.Capacitor?.isNativePlatform?.()) {
      // eslint-disable-next-line import/no-unresolved
      capacitorHapticsModule = await import(/* webpackIgnore: true */ '@capacitor/haptics').catch(() => null);
    }
  } catch (e) { /* ignore */ }
  return capacitorHapticsModule;
}

const VIBRATE_MAP = {
  light: 10,
  medium: 20,
  heavy: 30,
  success: [10, 30, 10],
  warning: [20, 40, 20],
  error: [30, 50, 30],
  selection: 5,
};

export async function haptic(style = 'light') {
  // Try Capacitor first (real iOS haptic engine)
  const cap = await getCapacitorHaptics();
  if (cap?.Haptics) {
    try {
      const map = {
        light: { style: cap.ImpactStyle?.Light },
        medium: { style: cap.ImpactStyle?.Medium },
        heavy: { style: cap.ImpactStyle?.Heavy },
      };
      if (style === 'success' || style === 'warning' || style === 'error') {
        await cap.Haptics.notification({ type: cap.NotificationType?.[style.charAt(0).toUpperCase() + style.slice(1)] });
        return;
      }
      if (style === 'selection') {
        await cap.Haptics.selectionStart();
        return;
      }
      const opts = map[style] || map.light;
      await cap.Haptics.impact(opts);
      return;
    } catch (e) { /* fall through */ }
  }

  // Fall back to vibrate API
  if (typeof navigator !== 'undefined' && navigator.vibrate) {
    try {
      navigator.vibrate(VIBRATE_MAP[style] || VIBRATE_MAP.light);
    } catch (e) { /* ignore */ }
  }
}
